import React, { useEffect, useState } from 'react';
import cuid from 'cuid';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  Stack,
  InputAdornment,
  IconButton,
  Grid,
  Alert,
  Link,
  Typography,
  Checkbox,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useAuth from 'src/hooks/useAuth';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import Iconify from 'src/components/Iconify';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { PATH_AUTH } from 'src/routes/paths';
import { AuthOptions, UserRoleTypes } from 'src/@types/auth';
import {
  TERMS_OF_USE,
  CA_PRIVACY_NOTICE,
  PRIVACY_POLICY,
} from 'src/portals/components/compliance/config';
import { ESignConsentAgreement } from 'src/portals/components/dialogs';
import queryString from 'query-string';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';

export type FormValuesProps = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  legalBusinessName: string;
  entityId: string;
  referralCode?: string;
  afterSubmit?: string;
};

export type RegisterFormProps = {
  role: UserRoleTypes;
  showLegalAgreements?: boolean;
  isExistingClient?: boolean;
  onRegistrationComplete?: () => void;
};

export type MarketingQueryParamsProps = {
  referralCode?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  gclid?: string;
};

export const parseMarketingQueryParams = (search: string) => {
  const marketingParams = queryString.parse(search, {
    decode: false,
  }) as MarketingQueryParamsProps;

  return {
    referralCode: marketingParams?.referralCode ?? '',
    utmSource: marketingParams?.utm_source ?? '',
    utmMedium: marketingParams?.utm_medium ?? '',
    utmCampaign: marketingParams?.utm_campaign ?? '',
    utmTerm: marketingParams?.utm_term ?? '',
    marketingId: marketingParams?.gclid ?? '',
  } as AuthOptions;
};

type PasswordRequirement = {
  regex: RegExp;
  text: string;
};

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { regex: /.{8,}/, text: 'Minimum length of 8 characters' },
  { regex: /[A-Z]/, text: 'At least one uppercase letter' },
  { regex: /[a-z]/, text: 'At least one lowercase letter' },
  { regex: /[0-9]/, text: 'At least one numeric digit' },
  { regex: /[^A-Za-z0-9]/, text: 'At least one special character' },
];

export default function RegisterForm({
  role,
  showLegalAgreements = false,
  isExistingClient = false,
  onRegistrationComplete,
}: RegisterFormProps) {
  const { register } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { search } = useLocation();
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);
  const [acceptedESignConsentAgreement, setAcceptedESignConsentAgreement] = useState(
    !showLegalAgreements
  );
  const [showESignConsentAgreement, setShowESignConsentAgreement] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [password, setPassword] = useState('');

  const RegisterSchema = Yup.object().shape({
    firstName: Yup.string().required('First name required'),
    lastName: Yup.string().required('Last name required'),
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string()
      .required('Password is required')
      .test('password-requirements', 'Password does not meet requirements', (value) => {
        if (!value) return false;
        return PASSWORD_REQUIREMENTS.every((req) => req.regex.test(value));
      }),
    legalBusinessName: Yup.string().required('Business name is required'),
    entityId: Yup.string(),
  });

  const options = parseMarketingQueryParams(search);
  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: isExistingClient ? `L1${cuid()}!` : '',
    legalBusinessName: '',
    referralCode: options.referralCode ? options.referralCode : '',
    role,
    entityId: '',
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const handleShowESignConsentAgreementDialog = () => {
    setShowESignConsentAgreement(true);
  };

  const handleAcceptedESignConsentAgreement = () => {
    setAcceptedESignConsentAgreement(true);
    setShowESignConsentAgreement(false);
  };

  const handleDeclinedESignConsentAgreement = () => {
    setAcceptedESignConsentAgreement(false);
    setShowESignConsentAgreement(false);
  };

  const {
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const options = parseMarketingQueryParams(search);
      await register(
        data.legalBusinessName,
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        data.entityId,
        role,
        {
          ...options,
          referralCode: data?.referralCode || '',
        }
      );

      enqueueSnackbar('Successfully Registered');
      if (!isExistingClient) {
        navigate(`${PATH_AUTH.registerSuccess}${search}`);
      } else {
        onRegistrationComplete?.();
      }
    } catch (error) {
      if (isMountedRef.current) {
        let message = 'Registration Error: Please try again or contact support';
        let errorSummary = '';
        if (error.status === 400) {
          if (error.errorCauses.length > 0) {
            errorSummary = error.errorCauses[0];
          }
        }
        if (error.response.status === 889) {
          message =
            'This email has already been registered. Please use a different email address or try logging in.';
        }

        console.error(errorSummary, error);
        setError('afterSubmit', { ...error, message });
      }
    }
  };

  const checkPasswordRequirement = (requirement: PasswordRequirement) =>
    requirement.regex.test(password);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPassword(newValue);
    e.target.name = 'password';
    methods.setValue('password', newValue, { shouldValidate: true });
  };

  const isPasswordValid = () => PASSWORD_REQUIREMENTS.every((req) => req.regex.test(password));

  useEffect(() => {
    localStorage.setItem('loginContext', role);
  }, [role]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
        <RHFTextField
          name="legalBusinessName"
          label="Legal Business Name"
          required={true}
          InputLabelProps={{
            sx: { color: 'white', '&.Mui-focused': { color: 'white' } },
            shrink: true,
          }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <RHFTextField
            name="firstName"
            label="First name"
            required={true}
            InputLabelProps={{
              sx: { color: 'white', '&.Mui-focused': { color: 'white' } },
              shrink: true,
            }}
          />
          <RHFTextField
            name="lastName"
            label="Last name"
            required={true}
            InputLabelProps={{
              sx: { color: 'white', '&.Mui-focused': { color: 'white' } },
              shrink: true,
            }}
          />
        </Stack>
        <RHFTextField
          name="email"
          label="Email address"
          required={true}
          InputLabelProps={{
            sx: { color: 'white', '&.Mui-focused': { color: 'white' } },
            shrink: true,
          }}
        />
        <Box>
          <RHFTextField
            name="password"
            label="Password"
            id="registration-password"
            required={true}
            type={showPassword ? 'text' : 'password'}
            onChange={handlePasswordChange}
            onFocus={() => setShowPasswordRequirements(true)}
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" aria-labelledby="registration-password">
                  <IconButton
                    edge="end"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Show/Hide"
                    sx={{ right: '10px' }}
                  >
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              sx: { color: 'white', '&.Mui-focused': { color: 'white' } },
              shrink: true,
            }}
          />

          {showPasswordRequirements && (
            <Box sx={{ mt: 1, ml: 1 }}>
              {PASSWORD_REQUIREMENTS.map((requirement) => (
                <Box
                  key={requirement.text}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: checkPasswordRequirement(requirement) ? 'success.main' : 'error.main',
                  }}
                >
                  {checkPasswordRequirement(requirement) ? (
                    <CheckCircleOutlineIcon fontSize="small" />
                  ) : (
                    <CancelIcon fontSize="small" />
                  )}
                  <Typography variant="caption">{requirement.text}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {!isExistingClient && role === 'customer' && (
          <RHFTextField
            name="referralCode"
            label="Referral Code"
            required={false}
            InputLabelProps={{
              sx: { color: 'white', '&.Mui-focused': { color: 'white' } },
              shrink: true,
            }}
          />
        )}
        {isExistingClient && role === 'customer' && (
          <RHFTextField
            name="entityId"
            label="Company Id"
            InputLabelProps={{
              sx: { color: 'white', '&.Mui-focused': { color: 'white' } },
              shrink: true,
            }}
            required={false}
          />
        )}

        {isExistingClient && role === 'iso' && (
          <RHFTextField
            name="entityId"
            label="ISO Id"
            id="partner-entity-id"
            InputLabelProps={{
              sx: { color: 'white', '&.Mui-focused': { color: 'white' } },
              shrink: true,
            }}
            required={true}
          />
        )}

        {showLegalAgreements && (
          <Grid container alignItems="flex-start">
            <Grid item xs={1}>
              <Checkbox
                onChange={handleShowESignConsentAgreementDialog}
                checked={acceptedESignConsentAgreement}
                value={acceptedESignConsentAgreement}
                id={'esign-disclosure-consent'}
                aria-label={'esign-disclosure-consent'}
                inputProps={{
                  'aria-label': 'esign-disclosure-consent',
                }}
                sx={{
                  right: '8px',
                  color: 'white',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              />
            </Grid>
            <Grid item xs={11}>
              <Typography
                align={'left'}
                variant="body2"
                id="esign-disclosure-consent-info"
                sx={{ color: '#fff' }}
              >
                By checking here you agree that you have read the Libertas ESIGN Disclosure and
                Consent and agree to do business electronically
              </Typography>
            </Grid>
          </Grid>
        )}
        {showESignConsentAgreement && (
          <ESignConsentAgreement
            onAccept={handleAcceptedESignConsentAgreement}
            onDecline={handleDeclinedESignConsentAgreement}
          />
        )}
        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={!acceptedESignConsentAgreement || !isPasswordValid()}
          sx={{
            mt: 2,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Register
        </LoadingButton>
        {showLegalAgreements && (
          <>
            <Typography variant="body2" align="center" sx={{ mt: 4, color: '#fff' }}>
              By registering, I agree to Libertas Funding&nbsp;
              <Link
                underline="always"
                href={TERMS_OF_USE}
                sx={{ color: '#fff' }}
                rel="noreferrer"
                target="_blank"
              >
                Terms of Use
              </Link>
              {', '}
              <Link
                underline="always"
                href={PRIVACY_POLICY}
                sx={{ color: '#fff' }}
                rel="noreferrer"
                target="_blank"
              >
                Privacy Policy
              </Link>
              , and{' '}
              <Link
                underline="always"
                href={CA_PRIVACY_NOTICE}
                sx={{ color: '#fff' }}
                rel="noreferrer"
                target="_blank"
              >
                CA Privacy Notice
              </Link>
              .
            </Typography>
          </>
        )}
      </Stack>
    </FormProvider>
  );
}
